import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@eshop/shared'
import { kycApi } from '@eshop/shared'

interface AdminLayoutProps {
  children: React.ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [pendingKYCCount, setPendingKYCCount] = useState(0)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const pendingKYCs = await kycApi.getAllPending()
        setPendingKYCCount(pendingKYCs.length)
      } catch (error) {
        console.error('Error fetching pending KYC count:', error)
      }
    }

    if (user?.is_staff || user?.is_superuser) {
      fetchPendingCount()
    }
  }, [user])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const menuItems = [
    { icon: '📊', label: 'Dashboard', path: '/dashboard' },
    { icon: '👥', label: 'Vendedores', path: '/vendors' },
    { icon: '🛡️', label: 'Verificaciones KYC', path: '/kyc' },
    { icon: '📦', label: 'Todos los Productos', path: '/products' },
    { icon: '🏷️', label: 'Categorías', path: '/categories' },
    { icon: '🛒', label: 'Órdenes', path: '/orders' },
    { icon: '👨‍💼', label: 'Staff', path: '/staff' },
    { icon: '⚙️', label: 'Configuración', path: '/settings' },
  ]

  return (
    <div className="min-h-screen flex bg-neutral-100">
      {/* Sidebar - Nolavi Design System */}
      <aside
        className={`${
          isSidebarOpen ? 'w-72' : 'w-20'
        } bg-gradient-to-b from-neutral-900 to-neutral-800 text-white transition-all duration-300 flex flex-col`}
      >
        {/* Header */}
        <div className="px-6 py-5 flex items-center justify-between border-b border-neutral-700/50">
          {isSidebarOpen && (
            <h1 className="text-2xl font-bold tracking-tight">Nolavi Admin</h1>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-white hover:bg-neutral-700/50 p-2 rounded-md transition-colors"
          >
            {isSidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6">
          <ul className="space-y-1.5">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-neutral-700/50 transition-colors relative rounded-xl"
                >
                  <span className="text-xl">{item.icon}</span>
                  {isSidebarOpen && (
                    <span className="font-medium">{item.label}</span>
                  )}
                  {item.path === '/kyc' && pendingKYCCount > 0 && (
                    <span className="ml-auto bg-error-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {pendingKYCCount}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info */}
        <div className="border-t border-neutral-700/50 px-4 py-5">
          {isSidebarOpen ? (
            <div className="px-2">
              <p className="text-sm text-neutral-400">Admin</p>
              <p className="font-semibold truncate mt-0.5">{user?.email}</p>
              <button
                onClick={handleLogout}
                className="mt-4 w-full bg-neutral-700/50 hover:bg-neutral-600 text-white py-2.5 px-4 rounded-xl transition-colors font-medium"
              >
                Cerrar Sesión
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full text-xl hover:bg-neutral-700/50 p-2 rounded-xl transition-colors"
              title="Cerrar Sesión"
            >
              🚪
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-neutral-200">
          <div className="px-8 py-5 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-800">
                Panel de Administración
              </h2>
              <p className="text-sm text-neutral-500 mt-0.5">
                Gestión completa del sistema
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-semibold text-neutral-800">{user?.username || 'Admin'}</p>
                <p className="text-sm text-neutral-500">Administrador</p>
              </div>
              <div className="w-11 h-11 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                {user?.username?.[0]?.toUpperCase() || 'A'}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto bg-neutral-50">
          <div className="px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
